import React, { useState, useEffect } from 'react';
import { Button } from '../../../../../components/ui/button';
import { trainingFlowsApi } from '../../../services/trainingFlowsApi';
import { agentsApi } from '../../../services/agentsApi';
import { AgentTrainingFlow } from '../../../types/flowTypes';
import { toast } from '@/hooks/use-toast';
import { Check, Loader2 } from 'lucide-react';

interface AgentWorkflowsProps {
    agentId?: string;
}

const AgentWorkflows: React.FC<AgentWorkflowsProps> = ({ agentId }) => {
    const [workflows, setWorkflows] = useState<AgentTrainingFlow[]>([]);
    const [activeWorkflowId, setActiveWorkflowId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    useEffect(() => {
        if (agentId) {
            loadData();
        }
    }, [agentId]);

    const loadData = async () => {
        if (!agentId) return;

        setLoading(true);
        try {
            // Use listFlows() to get ALL flows (not just those without phone)
            const availableFlows = await trainingFlowsApi.listFlows();
            setWorkflows(availableFlows);

            // Get the agent's active workflow
            const agentData = await agentsApi.getAgent(agentId);
            setActiveWorkflowId(agentData?.active_workflow_id || null);
        } catch (error) {
            console.error('[AgentWorkflows] Error loading flows:', error);
            toast.error('Error al cargar flows');
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = async (workflowId: string) => {
        if (!agentId) return;

        setActionLoading(workflowId);
        try {
            await agentsApi.setActiveWorkflow(agentId, workflowId);
            toast.success('Flow activado correctamente');
            setActiveWorkflowId(workflowId);
        } catch (error) {
            console.error('[AgentWorkflows] Error activating flow:', error);
            toast.error('Error al activar el flow');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeactivate = async () => {
        if (!agentId || !activeWorkflowId) return;

        setActionLoading(activeWorkflowId);
        try {
            await agentsApi.clearActiveWorkflow(agentId);
            toast.success('Flow desactivado');
            setActiveWorkflowId(null);
        } catch (error) {
            console.error('[AgentWorkflows] Error deactivating flow:', error);
            toast.error('Error al desactivar el flow');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const activeWorkflow = workflows.find(w => w.id === activeWorkflowId);

    return (
        <div className="space-y-6 p-4">
            {/* Active Flow Section */}
            {activeWorkflow && (
                <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Check className="h-5 w-5 text-green-600" />
                            <h3 className="text-foreground font-semibold text-base">
                                Flow Activo
                            </h3>
                        </div>
                        <Button
                            onClick={handleDeactivate}
                            variant="outline"
                            size="sm"
                            disabled={actionLoading === activeWorkflow.id}
                            className="text-xs"
                        >
                            {actionLoading === activeWorkflow.id ? (
                                <>
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                    Desactivando...
                                </>
                            ) : (
                                'Desactivar'
                            )}
                        </Button>
                    </div>
                    <div className="mt-3">
                        <h4 className="font-medium text-foreground">{activeWorkflow.nombre}</h4>
                        {activeWorkflow.descripcion && (
                            <p className="text-sm text-muted-foreground mt-1">
                                {activeWorkflow.descripcion}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Warning if no flow */}
            {!activeWorkflow && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-yellow-800 font-medium mb-2">
                        Este agente no tiene un flow activo
                    </p>
                    <p className="text-yellow-700 text-sm">
                        Selecciona un flow de la lista para activarlo.
                    </p>
                </div>
            )}

            {/* Available Flows Section */}
            <div>
                <h3 className="text-foreground font-semibold text-base mb-4">
                    Flows Disponibles
                </h3>

                {workflows.length === 0 ? (
                    <div className="p-6 bg-gray-50 border border-gray-200 rounded-lg text-center">
                        <p className="text-muted-foreground">
                            No hay flows disponibles para activar.
                        </p>
                        <p className="text-sm text-muted-foreground mt-2">
                            Crea un flow desde la página de <strong>Flows</strong>.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {workflows.map((workflow) => {
                            const isActive = activeWorkflowId === workflow.id;

                            return (
                                <div
                                    key={workflow.id}
                                    className={`border rounded-lg p-4 transition-all ${isActive
                                        ? 'border-green-300 bg-green-50/50'
                                        : 'border-gray-200 bg-white hover:border-primary/50'
                                        }`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <h4 className="font-medium text-foreground flex items-center gap-2">
                                                {workflow.nombre}
                                                {isActive && (
                                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                                        Activo
                                                    </span>
                                                )}
                                            </h4>
                                            {workflow.descripcion && (
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    {workflow.descripcion}
                                                </p>
                                            )}
                                        </div>
                                        <Button
                                            onClick={() => handleActivate(workflow.id)}
                                            variant={isActive ? "outline" : "default"}
                                            size="sm"
                                            disabled={isActive || actionLoading === workflow.id}
                                            className="ml-3"
                                        >
                                            {actionLoading === workflow.id ? (
                                                <>
                                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                                    Activando...
                                                </>
                                            ) : isActive ? (
                                                'Activo'
                                            ) : (
                                                'Activar'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgentWorkflows;
